// @spec RSK-STR-003
// @spec RSK-STR-001
// @spec RSK-STR-004
// @spec RSK-STR-023
/**
 * TypeScript types for the risks domain.
 */

/**
 * Risk category values.
 */
export type RiskCategory = 'operational' | 'compliance' | 'strategic' | 'cybersecurity';

/**
 * Risk level values.
 */
export type RiskLevel = 'critical' | 'high' | 'medium' | 'low';

/**
 * Risk status values.
 */
export type RiskStatus =
  | 'draft'
  | 'pending_approval'
  | 'open'
  | 'in_treatment'
  | 'accepted'
  | 'closed';

/**
 * User reference for risk ownership.
 */
export interface RiskOwner {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  imageUrl?: string | null;
}

/**
 * Risk entity.
 */
export interface Risk {
  id: string;
  riskId: string;
  title: string;
  description: string;
  category: RiskCategory;
  subcategory?: string | null;

  // Calculated scores
  inherentLikelihood: number;
  inherentImpact: number;
  inherentRiskScore: number;
  inherentRiskLevel: RiskLevel;

  // Qualitative assessments
  likelihoodRationale?: string | null;
  impactRationale?: string | null;

  // Ownership
  ownerId: string;
  owner?: RiskOwner | null;

  // Status
  status: RiskStatus;

  // Source
  riskSource?: string | null;
  sourceReference?: string | null;

  // Relationships
  linkedAssetIds: string[];

  // Metadata
  tags: string[];
  customFields?: Record<string, unknown> | null;

  // Timestamps
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;

  organizationId: string;
}

/**
 * Input for creating a new risk.
 */
export interface CreateRiskInput {
  title: string;
  description: string;
  category: RiskCategory;
  subcategory?: string | null;
  inherentLikelihood: number;
  inherentImpact: number;
  likelihoodRationale?: string | null;
  impactRationale?: string | null;
  ownerId: string;
  riskSource?: string | null;
  sourceReference?: string | null;
  linkedAssetIds?: string[];
  tags?: string[];
  customFields?: Record<string, unknown>;
}

/**
 * Input for updating an existing risk.
 * All fields are optional - only provided fields are updated.
 */
export interface UpdateRiskInput {
  title?: string;
  description?: string;
  category?: RiskCategory;
  subcategory?: string | null;
  inherentLikelihood?: number;
  inherentImpact?: number;
  likelihoodRationale?: string | null;
  impactRationale?: string | null;
  ownerId?: string;
  reviewerId?: string | null;
  linkedAssetIds?: string[];
  tags?: string[];
  customFields?: Record<string, unknown>;
  /** Required when likelihood or impact changes */
  changeReason?: string;
}

/**
 * Extended risk entity with edit metadata.
 * Returned from update endpoints.
 */
export interface RiskDetail extends Risk {
  /** Whether the risk has linked incidents */
  hasLinkedIncidents: boolean;
  /** Whether the risk has an active treatment plan */
  hasActiveTreatment: boolean;
  /** Whether the score change requires approval */
  requiresApproval: boolean;
}

/**
 * Risk scale value (1-5).
 */
export type RiskScaleValue = 1 | 2 | 3 | 4 | 5;

/**
 * Likelihood scale descriptions.
 */
export const LIKELIHOOD_LABELS: Record<
  RiskScaleValue,
  { label: string; probability: string; description: string }
> = {
  1: { label: 'Rare', probability: '< 5%', description: 'Highly unlikely to occur' },
  2: {
    label: 'Unlikely',
    probability: '5-25%',
    description: 'May occur in exceptional circumstances',
  },
  3: { label: 'Possible', probability: '25-50%', description: 'Could occur at some time' },
  4: { label: 'Likely', probability: '50-75%', description: 'Will probably occur' },
  5: {
    label: 'Almost Certain',
    probability: '> 75%',
    description: 'Expected to occur in most circumstances',
  },
};

/**
 * Impact scale descriptions.
 */
export const IMPACT_LABELS: Record<RiskScaleValue, { label: string; description: string }> = {
  1: { label: 'Negligible', description: 'Minimal impact, easily absorbed' },
  2: { label: 'Minor', description: 'Limited impact, minor disruption' },
  3: { label: 'Moderate', description: 'Noticeable impact, some disruption' },
  4: { label: 'Major', description: 'Significant impact, major disruption' },
  5: { label: 'Severe', description: 'Critical impact, threatens operations' },
};

/**
 * Risk category options.
 */
export const RISK_CATEGORIES: { value: RiskCategory; label: string }[] = [
  { value: 'operational', label: 'Operational' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'strategic', label: 'Strategic' },
  { value: 'cybersecurity', label: 'Cybersecurity' },
];

/**
 * Calculate risk score from likelihood and impact.
 */
export function calculateRiskScore(likelihood: number, impact: number): number {
  return likelihood * impact;
}

/**
 * Get risk level from score.
 */
export function getRiskLevel(score: number): RiskLevel {
  if (score >= 20) return 'critical';
  if (score >= 12) return 'high';
  if (score >= 6) return 'medium';
  return 'low';
}

// ============================================================================
// List/Filter Types (RSK-STR-002)
// ============================================================================

/**
 * Sort field options for risk lists.
 */
export type RiskSortField = 'created_at' | 'updated_at' | 'inherent_risk_score' | 'title';

/**
 * Sort order options.
 */
export type SortOrder = 'asc' | 'desc';

/**
 * Filter options for risks query.
 */
export interface RiskFiltersState {
  category: RiskCategory | null;
  subcategory: string | null;
  status: RiskStatus[] | null;
  riskLevel: RiskLevel[] | null;
  search: string | null;
  tags: string[] | null;
  ownerId: string | null;
  sortBy: RiskSortField;
  sortOrder: SortOrder;
}

/**
 * Pagination info from API.
 */
export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

/**
 * Category counts for filter UI.
 */
export interface CategoryCounts {
  operational: number;
  compliance: number;
  strategic: number;
  cybersecurity: number;
}

/**
 * Response from list risks API.
 */
export interface RiskListResponse {
  items: Risk[];
  pagination: PaginationInfo;
  categoryCounts: CategoryCounts;
}

/**
 * Default filter values.
 */
export const DEFAULT_RISK_FILTERS: RiskFiltersState = {
  category: null,
  subcategory: null,
  status: null,
  riskLevel: null,
  search: null,
  tags: null,
  ownerId: null,
  sortBy: 'created_at',
  sortOrder: 'desc',
};

/**
 * Human-readable labels for risk categories.
 */
export const RISK_CATEGORY_LABELS: Record<RiskCategory, string> = {
  operational: 'Operational',
  compliance: 'Compliance',
  strategic: 'Strategic',
  cybersecurity: 'Cybersecurity',
};

/**
 * Human-readable labels for risk statuses.
 */
export const RISK_STATUS_LABELS: Record<RiskStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  open: 'Open',
  in_treatment: 'In Treatment',
  accepted: 'Accepted',
  closed: 'Closed',
};

/**
 * Human-readable labels for risk levels.
 */
export const RISK_LEVEL_LABELS: Record<RiskLevel, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/**
 * Risk level display info with colors.
 */
export const RISK_LEVEL_INFO: Record<RiskLevel, { label: string; color: string }> = {
  critical: { label: 'Critical', color: 'destructive' },
  high: { label: 'High', color: 'warning' },
  medium: { label: 'Medium', color: 'default' },
  low: { label: 'Low', color: 'success' },
};

/**
 * Risk status display info with colors.
 */
export const RISK_STATUS_INFO: Record<RiskStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'secondary' },
  pending_approval: { label: 'Pending Approval', color: 'warning' },
  open: { label: 'Open', color: 'default' },
  in_treatment: { label: 'In Treatment', color: 'warning' },
  accepted: { label: 'Accepted', color: 'success' },
  closed: { label: 'Closed', color: 'muted' },
};

/**
 * Human-readable labels for sort fields.
 */
export const RISK_SORT_FIELD_LABELS: Record<RiskSortField, string> = {
  created_at: 'Created At',
  updated_at: 'Updated At',
  inherent_risk_score: 'Risk Score',
  title: 'Title',
};

// ============================================================================
// Tag Types (RSK-STR-009)
// ============================================================================

/**
 * Tag with usage count.
 */
export interface TagInfo {
  name: string;
  count: number;
}

/**
 * Response for listing all tags.
 */
export interface TagListResponse {
  items: TagInfo[];
  total: number;
}

/**
 * Response for tag suggestions.
 */
export interface TagSuggestionsResponse {
  suggestions: TagInfo[];
}

/**
 * Sort field options for tags.
 */
export type TagSortField = 'name' | 'count';

/**
 * Request for bulk tag operations.
 */
export interface BulkTagRequest {
  riskIds: string[];
  addTags?: string[];
  removeTags?: string[];
}

/**
 * Result for a single risk in bulk tag operation.
 */
export interface BulkTagRiskResult {
  id: string;
  tags: string[];
}

/**
 * Response for bulk tag operation.
 */
export interface BulkTagResponse {
  updatedCount: number;
  risks: BulkTagRiskResult[];
}

/**
 * Request to rename a tag.
 */
export interface RenameTagRequest {
  newName: string;
}

/**
 * Response for tag rename operation.
 */
export interface RenameTagResponse {
  oldName: string;
  newName: string;
  affectedCount: number;
}

/**
 * Request to merge tags.
 */
export interface MergeTagsRequest {
  sourceTagNames: string[];
  targetTagName: string;
}

/**
 * Response for tag merge operation.
 */
export interface MergeTagsResponse {
  sourceTagNames: string[];
  targetTagName: string;
  affectedCount: number;
}

/**
 * Response for tag deletion.
 */
export interface DeleteTagResponse {
  deletedTag: string;
  affectedCount: number;
}

// ============================================================================
// Review Schedule Types (RSK-STR-023)
// ============================================================================

/**
 * Review frequency options for risk reviews.
 */
export type ReviewFrequency =
  | 'monthly'
  | 'quarterly'
  | 'semi_annual'
  | 'annual'
  | 'custom'
  | 'none';

/**
 * User info for reviewer display.
 */
export interface ReviewerInfo {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}

/**
 * Response from review schedule API.
 */
export interface ReviewScheduleResponse {
  riskId: string;
  reviewFrequency: ReviewFrequency | null;
  nextReviewDate: string | null;
  lastReviewedAt: string | null;
  lastReviewedBy: ReviewerInfo | null;
  reviewerId: string | null;
  reviewer: ReviewerInfo | null;
  reminderDays: number[];
  isScheduledReviewEnabled: boolean;
  taskLeadDays: number;
  customIntervalDays: number | null;
}

/**
 * Input for updating review schedule.
 */
export interface UpdateReviewScheduleInput {
  reviewFrequency?: ReviewFrequency | null;
  nextReviewDate?: string | null;
  reviewerId?: string | null;
  reminderDays?: number[];
  isScheduledReviewEnabled?: boolean;
  taskLeadDays?: number;
  customIntervalDays?: number | null;
}

/**
 * Human-readable labels for review frequencies.
 */
export const REVIEW_FREQUENCY_LABELS: Record<ReviewFrequency, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual',
  annual: 'Annual',
  custom: 'Custom',
  none: 'None',
};

/**
 * Default review frequencies by risk level.
 */
export const DEFAULT_REVIEW_FREQUENCY_BY_LEVEL: Record<RiskLevel, ReviewFrequency> = {
  critical: 'monthly',
  high: 'quarterly',
  medium: 'semi_annual',
  low: 'annual',
};

// ============================================================================
// Risk-Control Link Types (RSK-STR-017)
// ============================================================================

/**
 * Link type values for risk-control links.
 */
export type ControlLinkType = 'reduces_likelihood' | 'reduces_impact' | 'reduces_both';

/**
 * Control effectiveness rating values.
 */
export type ControlEffectivenessRating =
  | 'ineffective'
  | 'partially_effective'
  | 'effective'
  | 'highly_effective';

/**
 * Effectiveness values for residual risk calculation.
 */
export const EFFECTIVENESS_VALUES: Record<ControlEffectivenessRating | 'null', number> = {
  ineffective: 0.1,
  partially_effective: 0.4,
  effective: 0.7,
  highly_effective: 0.9,
  null: 0.0,
};

/**
 * Human-readable labels for effectiveness ratings.
 */
export const EFFECTIVENESS_LABELS: Record<ControlEffectivenessRating, string> = {
  ineffective: 'Ineffective',
  partially_effective: 'Partially Effective',
  effective: 'Effective',
  highly_effective: 'Highly Effective',
};

/**
 * Display info for effectiveness ratings with colors.
 * Uses valid Badge variant values: 'default' | 'destructive' | 'outline' | 'secondary'
 */
export const EFFECTIVENESS_INFO: Record<
  ControlEffectivenessRating,
  { label: string; color: 'destructive' | 'default' | 'outline' | 'secondary' }
> = {
  ineffective: { label: 'Ineffective', color: 'destructive' },
  partially_effective: { label: 'Partially Effective', color: 'secondary' },
  effective: { label: 'Effective', color: 'default' },
  highly_effective: { label: 'Highly Effective', color: 'outline' },
};

/**
 * Human-readable labels for link types.
 */
export const LINK_TYPE_LABELS: Record<ControlLinkType, string> = {
  reduces_likelihood: 'Reduces Likelihood',
  reduces_impact: 'Reduces Impact',
  reduces_both: 'Reduces Both',
};

/**
 * A regulation mapping entry from the Control's regulation_mappings JSONB.
 */
export interface RegulationMapping {
  regulation: string;
  article: string;
  description?: string | null;
}

/**
 * Linked control entity.
 */
export interface LinkedControl {
  id: string;
  riskId: string;
  controlId: string;
  controlCode: string;
  controlTitle: string;
  controlFramework: string;
  linkType: ControlLinkType;
  effectivenessRating: ControlEffectivenessRating | null;
  effectivenessValue: number | null;
  implementationStatus: string | null;
  notes: string | null;
  regulationMappings: RegulationMapping[];
  createdAt: string;
  createdBy: string;
}

/**
 * Response for listing linked controls.
 */
export interface RiskLinkedControlsResponse {
  items: LinkedControl[];
  combinedEffectiveness: number;
  controlCount: number;
}

/**
 * Request to link a control to a risk.
 */
export interface LinkControlRequest {
  controlId: string;
  linkType: ControlLinkType;
  notes?: string | null;
}

/**
 * Response after successfully linking a control.
 */
export interface LinkControlSuccessResponse extends LinkedControl {
  residualRiskUpdated: boolean;
  newResidualScore: number | null;
  newResidualLevel: RiskLevel | null;
}

/**
 * Request to unlink a control.
 */
export interface UnlinkControlRequest {
  reason: string;
}

/**
 * Response after unlinking a control.
 */
export interface UnlinkControlResponse {
  success: boolean;
  residualRiskUpdated: boolean;
  newResidualScore: number | null;
  newResidualLevel: RiskLevel | null;
}

// ============================================================================
// Treatment Plan Types (RSK-STR-016)
// ============================================================================

/**
 * Treatment plan status values.
 */
export type TreatmentPlanStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

/**
 * Treatment action type values.
 */
export type TreatmentActionType =
  | 'implement_control'
  | 'review_process'
  | 'train_staff'
  | 'update_policy'
  | 'acquire_tool'
  | 'other';

/**
 * Action priority values.
 */
export type ActionPriority = 'critical' | 'high' | 'medium' | 'low';

/**
 * Action status values.
 */
export type ActionStatus = 'not_started' | 'in_progress' | 'blocked' | 'completed' | 'cancelled';

/**
 * Effectiveness rating values.
 */
export type EffectivenessRating =
  | 'highly_effective'
  | 'effective'
  | 'partially_effective'
  | 'ineffective';

/**
 * User reference for treatment plan/action assignee.
 */
export interface TreatmentActionAssignee {
  id: string;
  displayName: string;
}

/**
 * Treatment action entity.
 */
export interface TreatmentAction {
  id: string;
  title: string;
  description: string;
  actionType: TreatmentActionType;
  assignee: TreatmentActionAssignee;
  dueDate: string;
  priority: ActionPriority;
  status: ActionStatus;
  completedAt: string | null;
  completedBy: TreatmentActionAssignee | null;
  completionNotes: string | null;
  linkedTaskId: string | null;
  linkedControlId: string | null;
}

/**
 * Treatment plan entity.
 */
export interface TreatmentPlan {
  id: string;
  riskId: string;
  title: string;
  description: string;
  strategy: 'mitigate';
  targetRiskLevel: RiskLevel;
  targetResidualScore: number | null;
  targetDate: string;
  status: TreatmentPlanStatus;
  progressPercentage: number;
  actions: TreatmentAction[];
  approvedAt: string | null;
  approvedBy: TreatmentActionAssignee | null;
  completedAt: string | null;
  completedBy: TreatmentActionAssignee | null;
  effectivenessRating: EffectivenessRating | null;
  createdAt: string;
  createdBy: TreatmentActionAssignee;
  updatedAt: string;
}

/**
 * Input for creating a treatment action.
 */
export interface CreateTreatmentActionInput {
  title: string;
  description: string;
  actionType: TreatmentActionType;
  assigneeId: string;
  dueDate: string;
  priority: ActionPriority;
}

/**
 * Input for creating a treatment plan.
 */
export interface CreateTreatmentPlanInput {
  title: string;
  description: string;
  targetRiskLevel: RiskLevel;
  targetResidualScore?: number | null;
  targetDate: string;
  actions: CreateTreatmentActionInput[];
}

/**
 * Human-readable labels for treatment action types.
 */
export const TREATMENT_ACTION_TYPE_LABELS: Record<TreatmentActionType, string> = {
  implement_control: 'Implement Control',
  review_process: 'Review Process',
  train_staff: 'Train Staff',
  update_policy: 'Update Policy',
  acquire_tool: 'Acquire Tool',
  other: 'Other',
};

/**
 * Human-readable labels for action priorities.
 */
export const ACTION_PRIORITY_LABELS: Record<ActionPriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/**
 * Human-readable labels for action statuses.
 */
export const ACTION_STATUS_LABELS: Record<ActionStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/**
 * Human-readable labels for treatment plan statuses.
 */
export const TREATMENT_PLAN_STATUS_LABELS: Record<TreatmentPlanStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/**
 * Action status display info with colors.
 */
export const ACTION_STATUS_INFO: Record<ActionStatus, { label: string; color: string }> = {
  not_started: { label: 'Not Started', color: 'secondary' },
  in_progress: { label: 'In Progress', color: 'warning' },
  blocked: { label: 'Blocked', color: 'destructive' },
  completed: { label: 'Completed', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'muted' },
};

/**
 * Action priority display info with colors.
 */
export const ACTION_PRIORITY_INFO: Record<ActionPriority, { label: string; color: string }> = {
  critical: { label: 'Critical', color: 'destructive' },
  high: { label: 'High', color: 'warning' },
  medium: { label: 'Medium', color: 'default' },
  low: { label: 'Low', color: 'success' },
};
